import { useState, useEffect } from 'react';
import { getPayments, refundBooking } from '../services/api';

const PAY_STATUS = {
  paid:     { label: 'Paid',     cls: 'badge-success' },
  refunded: { label: 'Refunded', cls: 'badge-info'    },
  created:  { label: 'Pending',  cls: 'badge-warning' },
  failed:   { label: 'Failed',   cls: 'badge-danger'  },
};

const BOOK_STATUS = {
  completed:   'badge-success',
  confirmed:   'badge-info',
  in_progress: 'badge-warning',
  cancelled:   'badge-danger',
  pending:     'badge-grey',
};

export default function Payments() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filter, setFilter]     = useState('all');
  const [search, setSearch]     = useState('');
  const [selected, setSelected] = useState(null);
  const [refunding, setRefunding] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try { setPayments(await getPayments()); } catch (e) {}
    setLoading(false);
  };

  const handleRefund = async (bookingId) => {
    if (!window.confirm(`Issue refund for Booking #${bookingId}? This will call Razorpay and cannot be undone.`)) return;
    setRefunding(bookingId);
    try {
      await refundBooking(bookingId);
      await load();
      setSelected(null);
    } catch (e) {
      alert(e.response?.data?.detail || 'Refund failed. Check Razorpay credentials.');
    }
    setRefunding(null);
  };

  const filtered = payments.filter(p => {
    const matchStatus = filter === 'all' || p.payment_status === filter;
    const q = search.toLowerCase();
    const matchSearch = !q ||
      p.customer_name?.toLowerCase().includes(q) ||
      p.customer_email?.toLowerCase().includes(q) ||
      p.service_name?.toLowerCase().includes(q) ||
      String(p.booking_id).includes(q) ||
      (p.display_id || '').toLowerCase().includes(q) ||
      (p.razorpay_payment_id || '').toLowerCase().includes(q) ||
      (p.razorpay_refund_id  || '').toLowerCase().includes(q);
    return matchStatus && matchSearch;
  });

  // Summary stats — only count paid (not refunded) for revenue
  const totalPaid     = payments.filter(p => p.payment_status === 'paid').reduce((s, p) => s + p.amount, 0);
  const totalRefunded = payments.filter(p => p.payment_status === 'refunded').reduce((s, p) => s + p.amount, 0);
  const totalRevenue  = payments.filter(p => p.payment_status === 'paid').reduce((s, p) => s + (p.platform_fee || 0), 0);
  const netRevenue    = totalRevenue; // refunded bookings have platform_fee=0 now
  const pendingCount  = payments.filter(p => p.payment_status === 'created').length;

  return (
    <div>
      <div className="page-header">
        <h1>💳 Payments & Refunds</h1>
        <p>Track all payment transactions and refund status</p>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        {[
          { label: 'Total Collected', value: `₹${totalPaid.toFixed(2)}`,     color: '#16a34a', bg: '#f0fdf4', icon: '💰' },
          { label: 'Total Refunded',  value: `₹${totalRefunded.toFixed(2)}`, color: '#dc2626', bg: '#fef2f2', icon: '↩️' },
          { label: 'Platform Revenue',value: `₹${netRevenue.toFixed(2)}`,    color: '#7c3aed', bg: '#f5f3ff', icon: '📈' },
          { label: 'Pending Payments',value: pendingCount,                    color: '#d97706', bg: '#fffbeb', icon: '⏳' },
        ].map(c => (
          <div key={c.label} style={{ background: c.bg, border: `1px solid ${c.color}22`, borderRadius: 12, padding: '16px 20px' }}>
            <div style={{ fontSize: 24 }}>{c.icon}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: c.color, marginTop: 4 }}>{c.value}</div>
            <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>{c.label}</div>
          </div>
        ))}
      </div>

      {/* Detail Modal */}
      {selected && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setSelected(null)}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: 480, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ margin: 0 }}>Payment #{selected.id} — Booking {selected.display_id || `#${selected.booking_id}`}</h2>
              <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer' }}>✕</button>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              {[
                ['Customer',        selected.customer_name],
                ['Email',           selected.customer_email],
                ['Service',         selected.service_name],
                ['Amount',          `₹${Number(selected.amount).toFixed(2)}`],
                ['Platform Fee',    `₹${Number(selected.platform_fee).toFixed(2)}`],
                ['Worker Earning',  `₹${Number(selected.worker_earning).toFixed(2)}`],
                ['Payment Status',  <span className={`badge ${PAY_STATUS[selected.payment_status]?.cls || 'badge-grey'}`}>{PAY_STATUS[selected.payment_status]?.label || selected.payment_status}</span>],
                ['Booking Status',  <span className={`badge ${BOOK_STATUS[selected.booking_status] || 'badge-grey'}`}>{selected.booking_status}</span>],
                ['Razorpay Order',  selected.razorpay_order_id   || '—'],
                ['Razorpay Payment',selected.razorpay_payment_id || '—'],
                ['Refund Amount',   selected.payment_status === 'refunded' ? `₹${Number(selected.amount).toFixed(2)}` : '—'],
                ['Refund ID',       selected.razorpay_refund_id || (selected.payment_status === 'refunded' ? '✅ Processed' : '—')],
                ['Created',         selected.created_at?.substring(0, 19).replace('T', ' ')],
                ['Last Updated',    selected.updated_at?.substring(0, 19).replace('T', ' ')],
              ].map(([k, v]) => (
                <tr key={k} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '8px 4px', color: '#666', width: 140, fontWeight: 500 }}>{k}</td>
                  <td style={{ padding: '8px 4px', wordBreak: 'break-all' }}>{v}</td>
                </tr>
              ))}
            </table>

            <div style={{ marginTop: 20, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              {selected.payment_status === 'paid' && (
                <button
                  className="btn btn-danger"
                  disabled={refunding === selected.booking_id}
                  onClick={() => handleRefund(selected.booking_id)}
                >
                  {refunding === selected.booking_id ? '⏳ Processing...' : '↩️ Issue Refund'}
                </button>
              )}
              {selected.payment_status === 'refunded' && (
                <div style={{ color: '#16a34a', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                  ✅ Refund Issued {selected.razorpay_refund_id ? `(${selected.razorpay_refund_id})` : ''}
                </div>
              )}
              <button className="btn btn-secondary" onClick={() => setSelected(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="card">
        <div className="card-header">
          <h2>All Transactions ({filtered.length})</h2>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input
              placeholder="Search customer, service, Razorpay ID..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ padding: '6px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 13, width: 260 }}
            />
            <select value={filter} onChange={e => setFilter(e.target.value)}
              style={{ padding: '6px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 13 }}>
              <option value="all">All Status</option>
              <option value="paid">Paid</option>
              <option value="refunded">Refunded</option>
              <option value="created">Pending</option>
              <option value="failed">Failed</option>
            </select>
            <button className="refresh-btn" onClick={load}>🔄 Refresh</button>
          </div>
        </div>

        {loading ? <div className="loading">Loading payments...</div> : filtered.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#999', padding: 32 }}>No payments found</p>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Booking</th>
                  <th>Customer</th>
                  <th>Service</th>
                  <th>Amount</th>
                  <th>Platform Fee</th>
                  <th>Payment</th>
                  <th>Refund</th>
                  <th>Booking</th>
                  <th>Date</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(p => (
                  <tr key={p.id}>
                    <td>{p.id}</td>
                    <td>{p.display_id || `#${p.booking_id}`}</td>
                    <td>
                      <div style={{ fontWeight: 500 }}>{p.customer_name}</div>
                      <div style={{ fontSize: 11, color: '#999' }}>{p.customer_email}</div>
                    </td>
                    <td>{p.service_name}</td>
                    <td><strong>₹{Number(p.amount).toFixed(2)}</strong></td>
                    <td style={{ color: '#7c3aed' }}>₹{Number(p.platform_fee).toFixed(2)}</td>
                    <td>
                      <span className={`badge ${PAY_STATUS[p.payment_status]?.cls || 'badge-grey'}`}>
                        {PAY_STATUS[p.payment_status]?.label || p.payment_status}
                      </span>
                    </td>
                    <td>
                      {p.payment_status === 'refunded'
                        ? <div>
                            <span className="badge badge-info">✅ Refunded</span>
                            <div style={{ fontSize: 12, color: '#dc2626', fontWeight: 600, marginTop: 2 }}>
                              ₹{Number(p.amount).toFixed(2)}
                            </div>
                            {p.razorpay_refund_id && (
                              <div style={{ fontSize: 10, color: '#999' }} title={p.razorpay_refund_id}>
                                ID: {p.razorpay_refund_id.substring(0, 12)}...
                              </div>
                            )}
                          </div>
                        : p.payment_status === 'paid'
                          ? <span className="badge badge-grey">Not Refunded</span>
                          : <span style={{ color: '#ccc' }}>—</span>
                      }
                    </td>
                    <td>
                      <span className={`badge ${BOOK_STATUS[p.booking_status] || 'badge-grey'}`}>
                        {p.booking_status}
                      </span>
                    </td>
                    <td style={{ fontSize: 12, color: '#666' }}>{p.created_at?.substring(0, 10)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="btn btn-sm btn-primary" onClick={() => setSelected(p)}>🔍 Details</button>
                        {p.payment_status === 'paid' && (
                          <button
                            className="btn btn-sm btn-danger"
                            disabled={refunding === p.booking_id}
                            onClick={() => handleRefund(p.booking_id)}
                          >
                            {refunding === p.booking_id ? '⏳' : '↩️ Refund'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
